import { useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';
import { selectSessionAdvisor } from '@client/model/selectors';
import { formBuilderApi } from '../api/quote-and-apply-builder-api';
import { generatePreviewFormData } from '../utils/preview-form-data';

interface UseSendTestEnvelopeParams {
  formId: string;
  formDefinition: PolicyFormDefinition;
  formData: PolicyFormSubmissionData;
}

export function useSendTestEnvelope({
  formId,
  formDefinition,
  formData,
}: UseSendTestEnvelopeParams) {
  const advisor = useSelector(selectSessionAdvisor);
  const [isPending, setIsPending] = useState(false);

  async function sendTestEnvelope(roleEmails?: Record<string, string>): Promise<boolean> {
    if (!advisor) return false;

    setIsPending(true);

    const submissionData =
      Object.keys(formData).length > 0 ? formData : generatePreviewFormData(formDefinition);

    try {
      const result = await formBuilderApi.sendTestEnvelope(formId, {
        submissionData,
        recipientEmail: advisor.advisorEmail,
        recipientName: `${advisor.advisorFirstName} ${advisor.advisorLastName}`,
        roleEmails,
      });

      const emailList = result.recipients.map(r => r.email).join(', ');
      toast.success('Test envelope sent', {
        description: `DocuSign envelope sent to: ${emailList}`,
        duration: 8000,
      });

      return true;
    } catch (err: any) {
      toast.error('Failed to send envelope', {
        description: err?.message ?? 'An unexpected error occurred',
      });

      return false;
    } finally {
      setIsPending(false);
    }
  }

  return { sendTestEnvelope, isPending };
}
